import type { CountryCode, Filters } from './types';

export interface AdministrativeRegion { code: string; name: string }
export const US_STATES: readonly AdministrativeRegion[] = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
].map(([code,name]) => ({code:code!,name:name!}));
export const CANADA_PROVINCES: readonly AdministrativeRegion[] = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],['NT','Northwest Territories'],['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],['SK','Saskatchewan'],['YT','Yukon'],
].map(([code,name]) => ({code:code!,name:name!}));
export interface MarketConfiguration {
  code: CountryCode; name: string; currency: string; currencySymbol: string; locale: string;
  mapCentre: readonly [number,number]; defaultSearch: string; states: readonly AdministrativeRegion[];
  catalogSupported: boolean; privateCandidatesSupported: boolean;
}
export const US: MarketConfiguration = {
  code:'US',name:'United States',currency:'USD',currencySymbol:'US$',locale:'en-US',
  mapCentre:[39.5,-98.35],defaultSearch:'At least 5 acres with room for a garden and dogs, under USD 800k',states:US_STATES,
  catalogSupported:true,privateCandidatesSupported:true,
};
export const CANADA: MarketConfiguration = {
  code:'CA',name:'Canada',currency:'CAD',currencySymbol:'CA$',locale:'en-CA',
  mapCentre:[56.1,-106.35],defaultSearch:'Add a Canadian property to your private assessment',states:CANADA_PROVINCES,
  catalogSupported:false,privateCandidatesSupported:true,
};
const HISTORICAL_MALAYSIA: MarketConfiguration = {
  code:'MY',name:'Malaysia',currency:'MYR',currencySymbol:'RM',locale:'en-MY',mapCentre:[4.3,108.5],
  defaultSearch:'',states:[],catalogSupported:false,privateCandidatesSupported:false,
};
export const ACTIVE_MARKET = US;
export function marketFor(country: CountryCode): MarketConfiguration {
  return country === 'CA' ? CANADA : country === 'MY' ? HISTORICAL_MALAYSIA : US;
}
export const DEFAULT_FILTERS: Filters = {
  country:'US',currency:'USD',maxPrice:null,maxPricePerAcre:null,minAcres:1,
  home:'nice',ownership:'any',titleRestriction:'any',walking:'any',internet:'any',
  noise:[],plants:[],weights:{},requirements:[],location:'',
};
/** Independent market defaults. Supplied amounts always keep their explicit currency. */
export function freshFiltersForMarket(country: CountryCode = 'US', base: Partial<Filters> = {}): Filters {
  return {
    ...DEFAULT_FILTERS,country,currency:marketFor(country).currency,...base,
    noise:[...(base.noise??[])],plants:(base.plants??[]).map(plant=>({...plant})),weights:{...(base.weights??{})},
    requirements:(base.requirements??[]).map(requirement=>({...requirement,sourceObjectIds:[...requirement.sourceObjectIds]})),
  };
}
export function stateCodeFor(country: CountryCode, value: string): string | null {
  const normalized=value.trim().toLowerCase();
  return marketFor(country).states.find(state=>state.code.toLowerCase()===normalized||state.name.toLowerCase()===normalized)?.code??null;
}
export function formatPrice(value: number | null, currency = 'USD'): string {
  if(value===null)return 'Price unknown';
  return `${currency==='MYR'?'RM':currency} ${new Intl.NumberFormat(currency==='CAD'?'en-CA':currency==='MYR'?'en-MY':'en-US',{maximumFractionDigits:0}).format(value)}`;
}
export function formatAcres(value: number | null): string {
  return value===null?'Land area unknown':`${new Intl.NumberFormat('en-US',{maximumFractionDigits:2}).format(value)} acres`;
}
